import React, { type JSX, useState } from "react";
import { t } from "ttag";

import * as userAPI from "../../../api/user";
import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { strings } from "../../../localization";
import { CompleteOrderPaymentInitData } from "../../../models/checkout";
import { IUser } from "../../../models/user";
import { check } from "../../../models/utils";
import { objectKeys } from "../../../utils/functional";
import { setUser } from "../../common/reducers";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { BillingAddressSummary } from "../checkout-steps/BillingAddressSummary";
import { CheckoutStep } from "../checkout-steps/CheckoutStepContainer";
import { EmailAddressSummary } from "../checkout-steps/EmailAddressSummary";
import { PaymentMethods } from "../checkout-steps/PaymentMethods";
import { ShippingAddressSummary } from "../checkout-steps/ShippingAddressSummary";
import { CSRHeader } from "../components/CSRHeader";
import { OrderSummary } from "../components/OrderSummary";
import { PaymentChildFrameListener } from "../components/PaymentChildFrameListener";
import { Step } from "../constants";
import {
    beginCompleteDeferredPayment,
    setFields,
    updateFinancingPlans,
    updatePaymentError,
} from "../reducers";
import {
    completeDeferredPayment as completeDeferredPaymentThunk,
    loadAssistedUser,
    loadCountries,
    loadEmailAddress,
    loadPaymentMethods,
    loadUserAddresses,
} from "../thunks";
import { ICheckoutHTTPError } from "../types";
import {
    IErrorMsgTemplateContext,
    filterFinancingPlans,
    renderErrorMessageTemplate,
    sortFinancingPlans,
} from "../utils";

interface IStepErrors {
    global?: string[];
    payment_method?: string[];
}

const parseInitData = (initDataJSON: string) => {
    return check(CompleteOrderPaymentInitData.decode(JSON.parse(initDataJSON)));
};

const renderStepLogin = (user: IUser | null): JSX.Element | null => {
    // Only display the email step if the user already has an email address. Otherwise,
    // it becomes part of the shipping address step.
    if (user && user.email && !user.is_csr) {
        return (
            <CheckoutStep
                key={Step.LOGIN}
                className="checkout-step--email checkout-step--first"
                heading={t`Email Address`}
            >
                <EmailAddressSummary user={user} />
            </CheckoutStep>
        );
    }
    return null;
};

const renderStepShippingAddress = (idx: number): JSX.Element => {
    return (
        <CheckoutStep
            key={Step.SHIPPING_ADDRESS}
            className="checkout-step--shipping-address form"
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_SHIPPING_ADDRESS",
            )}`}
        >
            <ShippingAddressSummary />
        </CheckoutStep>
    );
};

const renderStepBillingAddress = (idx: number): JSX.Element => {
    return (
        <CheckoutStep
            key={Step.BILLING_ADDRESS}
            className="checkout-step--billing-address form"
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_BILLING_ADDRESS",
            )}`}
        >
            <BillingAddressSummary />
        </CheckoutStep>
    );
};

const renderStepPaymentMethod = (
    idx: number,
    user: IUser | null,
    messages: IStepErrors,
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null,
    enableSplitPay: boolean | undefined,
    onPlaceOrder: () => void,
): JSX.Element => {
    const isCSR = user && user.is_csr;
    return (
        <PaymentMethods
            key={Step.PAYMENT_METHODS}
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_PAYMENT_METHODS",
            )}`}
            errors={messages.payment_method || []}
            buildFinancingUpsell={buildFinancingUpsell}
            enableSplitPay={enableSplitPay ? true : false}
            isCSR={!!isCSR}
            onContinue={onPlaceOrder}
        />
    );
};

const renderStep = (
    idx: number,
    step: Step,
    user: IUser | null,
    messages: IStepErrors,
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null,
    enableSplitPay: boolean | undefined,
    onPlaceOrder: () => void,
): JSX.Element | null => {
    switch (step) {
        case Step.LOGIN:
            return renderStepLogin(user);
        case Step.SHIPPING_ADDRESS:
            return renderStepShippingAddress(idx);
        case Step.BILLING_ADDRESS:
            return renderStepBillingAddress(idx);
        case Step.PAYMENT_METHODS:
            return renderStepPaymentMethod(
                idx,
                user,
                messages,
                buildFinancingUpsell,
                enableSplitPay,
                onPlaceOrder,
            );
        case Step.PLACING_ORDER:
        default:
            return null;
    }
};

export const CompleteDeferredPayment = (props: {
    initDataJSON: string;
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
    enableSplitPay?: boolean;
    disableFreeShippingMessage?: boolean;
}): React.ReactNode => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [messages, setMessages] = useState<IStepErrors>({});

    const user = useAppSelector((rootState) => rootState.common.user);

    const initData = parseInitData(props.initDataJSON);

    useMountEffect(() => {
        const loadData = async () => {
            // Load all the data necessary to begin checkout
            const [loadedUser] = await Promise.all([
                userAPI.load().then((u) => {
                    dispatch(setUser(u));
                    return u;
                }),
                dispatch(loadCountries()),
                dispatch(loadPaymentMethods()),
            ]);

            // Load assisted user if the active user is a CSR
            if (loadedUser.is_csr) {
                try {
                    await dispatch(loadAssistedUser()).unwrap();
                } catch (e) {
                    console.warn(e);
                }
            }

            // Load the email address associated with the basket. Has to be deliberately
            // after the call to loadAssistedUser to prevent a race-condition.
            await Promise.all([
                dispatch(loadEmailAddress()),
                dispatch(loadUserAddresses()),
            ]);

            const sortedPlans = sortFinancingPlans(
                filterFinancingPlans(initData.financing_plans),
            );
            dispatch(updateFinancingPlans(sortedPlans));

            // Import shipping address
            const shippingFields: Record<string, unknown> = {};
            objectKeys(initData.shipping_address).forEach((key) => {
                const fieldKey = `shipping_${key}`;
                shippingFields[fieldKey] = initData.shipping_address[key];
            });
            dispatch(setFields(shippingFields));

            // Import billing address
            const billingFields: Record<string, unknown> = {};
            objectKeys(initData.billing_address).forEach((key) => {
                const fieldKey = `billing_${key}`;
                billingFields[fieldKey] = initData.billing_address[key];
            });
            dispatch(setFields(billingFields));

            // Load the order data into Redux
            dispatch(beginCompleteDeferredPayment({ order: initData.order }));
            // Jump to the payment step
            dispatch(
                setFields({
                    current_step: Step.PAYMENT_METHODS,
                }),
            );

            // Loading is complete
            setIsLoading(false);
        };

        void loadData();
    });

    const onCheckoutError = (err: ICheckoutHTTPError) => {
        console.error(err);

        const errors = (err && err.response ? err.response.body : {}) || {};
        const errorMessages: IStepErrors = {};
        const ctx: IErrorMsgTemplateContext = {
            orderID: initData.order.number,
        };
        if (errors.global) {
            dispatch(updatePaymentError(errors.global[0]));
        } else if (errors.payment) {
            errorMessages.payment_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_PAYMENT_METHOD") || "",
                    ctx,
                ),
            ].concat(errors.payment);
        } else {
            errorMessages.payment_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_FALLBACK") || "",
                    ctx,
                ),
            ];
        }
        setMessages(errorMessages);
    };

    const onPlaceOrder = () => {
        dispatch(
            completeDeferredPaymentThunk({
                user,
                basketToken: initData.basket.token,
                orderToken: initData.order.token,
            }),
        )
            .unwrap()
            .catch((err) => {
                onCheckoutError(err);
            });
    };

    if (isLoading) {
        return (
            <div className="checkout__react-container">
                <LoadingSpinner />
            </div>
        );
    }

    const formSteps = [
        Step.LOGIN,
        Step.SHIPPING_ADDRESS,
        Step.BILLING_ADDRESS,
        Step.PAYMENT_METHODS,
        Step.PLACING_ORDER,
    ];

    return (
        <div className="checkout__react-container">
            <div className="checkout__column--left">
                <CSRHeader user={user} />
                {formSteps.map((step, idx) =>
                    renderStep(
                        idx,
                        step,
                        user,
                        messages,
                        props.buildFinancingUpsell,
                        props.enableSplitPay,
                        onPlaceOrder,
                    ),
                )}
            </div>
            <div className="checkout__column--right basket-summary">
                <h2 className="basket-summary__header">{t`Order Summary`}</h2>
                <OrderSummary
                    order={initData.order}
                    disableFreeShippingMessage={
                        props.disableFreeShippingMessage
                    }
                />
            </div>
            <PaymentChildFrameListener
                onCheckoutError={onCheckoutError}
                hasPaymentMethodError={!!messages.payment_method}
            />
        </div>
    );
};
